import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { themeGet } from '@styled-system/theme-get';

import { imgOnError } from '../../../../shared/helpers';
import { useReportsQuiz } from '../data';
import { useTileStatusTabs } from '../../hooks/useTileStatusTabs';
import {
  Th,
  TableIcon,
  Td as StyledTd,
  Tr,
  ShadedTr,
} from '../../../../shared/TableComponents/StyledComponents';
import Card from '../../../../shared/Card';
import Placeholder from '../../../../shared/Placeholder';
import Tooltip from '../../../../shared/Tooltip';
import Text from '../../../../shared/Text';
import PercentBar from '../../../../shared/PercentBar';
import Sort from '../../../../shared/Sort';

const QuizTable = ({ data, changeSort, sortingStatus }) => {
  const { t } = useTranslation('reports');

  return (
    <div style={{ width: '100%', paddingTop: '15px' }}>
      <Tooltip id="Quiz" />

      <Table>
        <thead>
          <Tr>
            <Th style={{ width: '72px' }}>
              <Text
                variant="body3"
                data-tip={t('quizTiles.tableColumns.tileTooltip')}
                data-for="Quiz"
              >
                {t('quizTiles.tableColumns.tile')}
              </Text>
            </Th>
            <Th>
              <SortWrapper onClick={() => changeSort('headline')}>
                <Sort sortingStatus={sortingStatus} columnName="headline" />
                <Text
                  data-tip={t('quizTiles.tableColumns.headlineTooltip')}
                  data-for="Quiz"
                  variant="body3"
                >
                  {t('quizTiles.tableColumns.headline')}
                </Text>
              </SortWrapper>
            </Th>
            <Th>
              <Text
                data-tip={t('quizTiles.tableColumns.questionTooltip')}
                data-for="Quiz"
                variant="body3"
              >
                {t('quizTiles.tableColumns.question')}
              </Text>
            </Th>
            <Th>
              <Text
                data-tip={t('quizTiles.tableColumns.answersTooltip')}
                data-for="Quiz"
                variant="body3"
              >
                {t('quizTiles.tableColumns.answers')}
              </Text>
            </Th>
          </Tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <ShadedTr
              key={i}
              className={`stat_action js-open-tile-stats-modal`}
              data-tile-id={row.id}
              data-href={`/client_admin/tiles/${row.id}/tile_stats`}
              data-tab="Reports tab"
              data-sub-tab="Quiz Tiles"
              style={{ cursor: 'pointer' }}
            >
              <Td textAlign="left">
                <Icon src={row.tileImage} onError={imgOnError} />
              </Td>
              <Td>
                <Text
                  as="a"
                  display="inline-block"
                  color="primary.100"
                  my="12px"
                  variant="small1"
                >
                  {row.headline}
                </Text>
              </Td>
              <Td>
                <Text as="p" my="12px" variant="small1">
                  {row.question}
                </Text>
              </Td>
              <Td>
                <Answers>
                  <AnswerWrapper key={row.correctAnswer.index}>
                    <Text variant="small1" mr="auto">
                      {row.correctAnswer.text}
                    </Text>
                    <PercentageWrapper>
                      <PercentBar
                        percentage={`${row.correctAnswer.percentage}%`}
                        color={'success.default'}
                      />
                      <Text variant="small3" mr="auto">
                        {t('quizTiles.correct')}
                      </Text>
                    </PercentageWrapper>
                    <Text variant="small1" width="48px" textAlign="right">
                      {row.correctAnswer.amount}
                    </Text>
                  </AnswerWrapper>
                  {row.wrongAnswers.map(
                    ({ text, amount, percentage, index }) => (
                      <AnswerWrapper key={index}>
                        <Text variant="small1" mr="auto">
                          {text}
                        </Text>
                        <PercentageWrapper>
                          <PercentBar
                            percentage={`${percentage}%`}
                            color={'error.default'}
                          />
                          <Text variant="small3" mr="auto">
                            {t('quizTiles.incorrect')}
                          </Text>
                        </PercentageWrapper>
                        <Text variant="small1" width="48px" textAlign="right">
                          {amount}
                        </Text>
                      </AnswerWrapper>
                    ),
                  )}
                  <TotalWrapper>
                    <Text variant="button">{t('quizTiles.totalAnswers')}</Text>
                    <Text variant="button" textAlign="right">
                      {row.totalAnswersCount}
                    </Text>
                  </TotalWrapper>
                </Answers>
              </Td>
            </ShadedTr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export const Quiz = ({
  onClick,
  showAll,
  campaignId,
  sortingStatus,
  changeSort,
}) => {
  const {
    activeTab,
    tileStatus,
    page,
    tabs,
    setPage,
    handleTabClick,
  } = useTileStatusTabs('quiz');
  const { t } = useTranslation('reports');
  const itemsPerPage = 5;
  const onPageChange = data => setPage(data.selected + 1);
  const { isLoading, data } = useReportsQuiz({
    page,
    itemsPerPage,
    showAll,
    campaignId,
    tileStatus,
    ...sortingStatus,
  });

  const { pagy } = data;

  return (
    <Card
      items={!showAll && itemsPerPage}
      pagy={!showAll && data.data.length > 0 ? pagy : null}
      title={t('quizTiles.tableTitle')}
      titleTooltip={t('quizTiles.tableTitleTooltip')}
      onClick={onClick}
      canShowAll={data.data.length > 0}
      isShowingAll={showAll}
      isLoading={isLoading}
      onPageChange={onPageChange}
      className="quiz-section"
      tabs={tabs}
      activeTab={activeTab}
      tabClick={handleTabClick}
      margin="0"
    >
      {data.data.length ? (
        <QuizTable
          sortingStatus={sortingStatus}
          changeSort={changeSort}
          data={data.data}
        />
      ) : (
        <Placeholder />
      )}
    </Card>
  );
};

const Table = styled.table`
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
`;

const Td = styled(StyledTd)`
  vertical-align: top;
  position: relative;
  top: 1px;
  &:after {
    content: '';
    position: absolute;
    top: -1px;
    left: 0px;
    height: 1px;
    width: 100%;
    background-color: ${themeGet('colors.black.10')};
  }
  &:first-child:after {
    width: calc(100% - 24px);
    right: 0px;
    left: auto;
  }
  &:last-child:after {
    width: calc(100% - 24px);
  }
`;

const AnswerWrapper = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: 1fr 100px 47px;
  padding: 8px 0;
  border-bottom: 1px solid;
  border-color: ${themeGet('colors.black.10')};

  &:first-child {
    padding: 0 0 8px;
  }
`;

const PercentageWrapper = styled.div`
  width: 97px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const Answers = styled.div`
  margin: 12px 0 0;
`;

const TotalWrapper = styled.div`
  display: flex;
  margin: 8px 0 24px;
  justify-content: space-between;
`;

const Icon = styled(TableIcon)`
  margin-top: 12px;
`;

const SortWrapper = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
  cursor: pointer;
  align-items: center;
  gap: 8px;
`;
